// Hook for creating Stripe payment intents (single-enrollment subscription payment)
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

export interface CreatePaymentIntentInput {
  enrollmentId: string;
  paymentMethodId?: string;
}

export interface PaymentIntentResponse {
  success: boolean;
  client_secret?: string;
  payment_intent_id?: string;
  amount?: number;
  platform_fee?: number;
  stripe_fee?: number;
  net_amount?: number;
  error?: string;
  requires_onboarding?: boolean;
}

export function useCreatePaymentIntent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreatePaymentIntentInput): Promise<PaymentIntentResponse> => {
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      if (!supabaseUrl) throw new Error('Supabase URL not configured');
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      const res = await fetch(`${supabaseUrl}/functions/v1/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(input),
      });

      const json = await res.json();

      if (!res.ok) {
        throw new Error(json?.error ?? 'Failed to create payment intent');
      }

      if (json.error) {
        throw new Error(json.error);
      }

      return json as PaymentIntentResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptionsWithAmountDue'] });
      queryClient.invalidateQueries({ queryKey: ['payment-records'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
    },
  });
}
